import { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Slider,
  TextField,
  Typography,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

interface CostSliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit?: string;
  description?: string;
  onChange: (value: number) => void;
  formatValue?: (value: number) => string;
  /** Warning message to show when value is 0 */
  warningWhenZero?: string;
}

export function CostSlider({
  label,
  value,
  min,
  max,
  step,
  unit = '',
  description,
  onChange,
  formatValue,
  warningWhenZero,
}: CostSliderProps) {
  const [inputValue, setInputValue] = useState(String(value));
  const [sliderMin, setSliderMin] = useState(min);
  const [sliderMax, setSliderMax] = useState(max);
  const [sliderStep, setSliderStep] = useState(step);

  // Update input when value changes externally
  useEffect(() => {
    setInputValue(String(value));
  }, [value]);

  // Adjust slider scale when value exceeds bounds
  const adjustScale = useCallback((newValue: number) => {
    if (newValue > sliderMax) {
      // Double the max and adjust step
      const newMax = Math.ceil(newValue * 1.5 / step) * step;
      setSliderMax(newMax);
      setSliderStep(Math.max(step, Math.floor(newMax / 100)));
    } else if (newValue < sliderMin && newValue >= 0) {
      setSliderMin(Math.max(0, newValue));
    }
  }, [sliderMax, sliderMin, step]);

  const handleSliderChange = (_: Event, newValue: number | number[]) => {
    const val = newValue as number;
    setInputValue(String(val));
    onChange(val);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleInputBlur = () => {
    let newValue = parseFloat(inputValue);
    if (isNaN(newValue)) {
      newValue = value;
    }
    newValue = Math.max(0, newValue);
    setInputValue(String(newValue));
    adjustScale(newValue);
    onChange(newValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleInputBlur();
    }
  };

  const showWarning = warningWhenZero && value === 0;

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2" fontWeight={500}>
            {label}
          </Typography>
          {showWarning && (
            <Tooltip title={warningWhenZero} arrow>
              <WarningAmberIcon
                sx={{ fontSize: 18, color: 'warning.main', cursor: 'help' }}
              />
            </Tooltip>
          )}
        </Box>
        <TextField
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          size="small"
          sx={{ width: 120 }}
          InputProps={{
            endAdornment: unit ? (
              <InputAdornment position="end">{unit}</InputAdornment>
            ) : undefined,
          }}
          inputProps={{
            style: { textAlign: 'right' },
          }}
        />
      </Box>
      {description && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          {description}
        </Typography>
      )}
      <Slider
        value={value}
        min={sliderMin}
        max={sliderMax}
        step={sliderStep}
        onChange={handleSliderChange}
        valueLabelDisplay="auto"
        valueLabelFormat={(v) => formatValue ? formatValue(v) : String(v)}
        sx={{
          '& .MuiSlider-valueLabel': {
            backgroundColor: 'primary.main',
          },
        }}
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: -0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {formatValue ? formatValue(sliderMin) : sliderMin}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatValue ? formatValue(sliderMax) : sliderMax}
        </Typography>
      </Box>
    </Box>
  );
}
